// REACT
import React, { useEffect } from "react";

// MATERIAL-UI
import { Grid, TableBody, TableCell, TableRow, Typography } from "@mui/material";

// COMPONENTS
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import GainsOrLoses from "src/components/common/GainsOrLoses";
import useTableSort from "src/utils/hooks/useTableSort";

// ASSETS
import { trackEvent } from "src/Amplitude";
import { Holding } from "src/components/wallets/common/types/holdings";
import { HoldingsModalStyles } from "src/components/wallets/Modal/Holdings/holdingModal.styles";
import { RootState, useAppSelector } from "src/store/store";
import formatCoinName from "src/utils/formatCoinName";
import { dynamicUnitFormatter, formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";

export interface Holdings {
  change: number;
  available: number;
  price: number;
  value: number;
  pendingValue: number;
  total: number;
  openTargets: number;
  reserved: number;
  code: string;
  id: string;
  name: string;
  logoUrl: string;
  memo: boolean;
  depositable: boolean;
  inactive: boolean;
  formatName?: string;
  totalValue: number;
}

interface TableBodyProps {
  data: Holdings[];
  order: "asc" | "desc";
  orderBy: string;
  onClick?: (holding: Holding) => void;
}

export interface OrganizedHoldingsData {
  code: string;
  logoUrl: string;
  name: string;
  value: number;
  price: number;
  change: number;
  available: number;
  memo: boolean;
  depositable: boolean;
  inactive: boolean;
  total: number;
  pendingValue: number;
  totalUnits: number;
}

const HoldingsTableBody = (props: TableBodyProps) => {
  const classes = HoldingsModalStyles();
  const [organizedData, setOrganizedData] = React.useState<OrganizedHoldingsData[]>([]);

  // redux - get users selected currency
  const { selectedCurrency } = useAppSelector((state: RootState) => state.settings);

  const [sort, compare] = useTableSort();

  useEffect(() => {
    let array = props.data.map((el) => ({
      code: el.code,
      logoUrl: el.logoUrl || el.code,
      name: el.name,
      value: el.value,
      price: el.price,
      change: el.change,
      formatName: el.formatName,
      available: el.available,
      memo: el.memo,
      depositable: el.depositable,
      inactive: el.inactive,
      total: el.totalValue,
      pendingValue: el.pendingValue,
      totalUnits: el.total,
    }));
    setOrganizedData(array);
  }, [props.data]);

  // format the price to be maximumSignificantDigits of 6.
  // this makes sure that there is always 6 didgets. this is good for currencies like shib that can have alot of zeros past decimal point
  const formatPrice = (price: number, currency: string): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "AUD",
      maximumSignificantDigits: 6,
    }).format(price);
  };

  return (
    <TableBody id="wallets-holdings-tablebody">
      {sort(organizedData, compare(props.order, props.orderBy)).map((el: any, idx: number) => (
        <TableRow
          hover
          role="checkbox"
          tabIndex={-1}
          onClick={() => {
            trackEvent(`Select_Wallets_Page_Coins_List`, {
              Token_Page: el.name,
            });
          }}
          className={classes.tableRow}
          key={el.code}>
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Grid container alignItems="center">
              <Grid item sx={{ marginLeft: "14px" }}>
                <CountryOrCryptoFlag name={el?.logoUrl || el.code} size={30} />
              </Grid>
            </Grid>
          </TableCell>

          <TableCell>
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.code2}>
                  {el.name ? formatCoinName(el.name) : "undefined"}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography>{el.code}</Typography>
              </Grid>
            </Grid>
          </TableCell>

          {/*Total Units */}
          <TableCell translate="no">{dynamicUnitFormatter(el.totalUnits, el.price)}</TableCell>

          {/*Available Units */}
          <TableCell translate="no">{dynamicUnitFormatter(el.available, el.price)}</TableCell>
          {/* PRICE */}
          <TableCell translate="no">{formatPrice(el.price, selectedCurrency || "AUD")}</TableCell>

          {/* Available */}
          <TableCell translate="no">
            {formatCurrency(el.value, selectedCurrency || "AUD")}
          </TableCell>

          {/* Total */}
          <TableCell translate="no">
            {formatCurrency(el.total, selectedCurrency || "AUD")}
          </TableCell>

          <TableCell>
            {" "}
            <GainsOrLoses balance={el.change} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
};

export default HoldingsTableBody;
